import {transferify} from 'shared/utils/data-service-utils';
import bind from 'bind-decorator';
import {ngAsync} from 'shared/utils/ng-@decorators';
import type {
    Form,
    PagedParameteredResult,
    SearchParams,
    StaticSearchParams,
    ViewableSearch
} from 'typings/portaro.be.types';
import type {AnyObject, StateManager} from 'typings/portaro.fe.types';
import type {Observable} from 'rxjs';
import {Subject} from 'rxjs';
import Pagination from './pagination';


/**
 * @name SearchManager
 * @class
 * @description this class initiates search,
 * can change search params and load new search results,
 * provides paginated results
 * can load forms independently of search
 */
export class SearchManager<PARAMS extends SearchParams = SearchParams, ITEM = any> {

    private readonly asJsonStringUrlSerializedFields = ['facetRestriction', 'qt'];
    private readonly pagination: any;
    private readonly deregisterUrlStateChange;
    private frontendParams: PARAMS = {} as PARAMS;
    private search: ViewableSearch<PARAMS, ITEM> = null;
    private forms: Form[] = [];
    private newSearchResultNotification$: Subject<void> = new Subject();

    /**
     * creates and initialize search manager
     *
     * @param $q
     * @param stateManager - shared urlStateManagerService or own memoryStateManager
     * @param staticParams staticke paramtery hledani
     * @param loadSearch funkce s parametrem params vracejici vysledky hledani
     * @param loadFormsFunction funkce s parametrem params vracejici pouze formulare pro hledani
     */
    constructor($q,
                private stateManager: StateManager,
                private staticParams: StaticSearchParams | AnyObject,
                private loadSearch: (params: PARAMS) => Promise<ViewableSearch<PARAMS, ITEM>>,
                private loadFormsFunction?: (_: StaticSearchParams | AnyObject) => Promise<Form[]>) {

        this.pagination = new Pagination($q, this.loadPageFunction, this.requestPageFunction);
        this.deregisterUrlStateChange = stateManager.register(this.onStateChange);
    }

     // this method uses new params to initiate state change (makes request)
     @ngAsync()
     private async requestParametersChange(newParams): Promise<void> {

        const transferable = transferify(newParams);
        this.asJsonStringUrlSerializedFields
            .filter((fieldName) => Object.hasOwn(transferable, fieldName))
            .forEach((fieldName) => transferable[fieldName] = JSON.stringify(transferable[fieldName]));

        await this.stateManager.requestChangeState(transferable);
    }

    // this function callback is used by pagination to request new results page
    // it changes parameter of current state (make request)
    @bind
    private async requestPageFunction(pageNumber: number): Promise<void> {
        await this.stateManager.requestChangeStateParameter('pageNumber', pageNumber);
    }

    // callback from state manager
    // updates pagination state (causes search initiation)
    @bind
    private async onStateChange(newParams: PARAMS): Promise<void> {
        this.asJsonStringUrlSerializedFields
            .filter((fieldName) => Object.hasOwn(newParams, fieldName))
            .forEach((fieldName) => newParams[fieldName] = JSON.parse(newParams[fieldName]));
        await this.pagination.loadPage(newParams.pageNumber, newParams);
    }

    // this callback function is used by pagination to initiate search and load new results from
    @bind
    @ngAsync()
    private async loadPageFunction(pageNumber: number, allParams: PARAMS): Promise<any> {

        const currentParams = {...this.staticParams, ...allParams};

        this.search = await this.loadSearch(currentParams)
        this.forms = this.search?.forms ?? this.forms;
        const result = this.search.result;

        this.frontendParams = {...this.frontendParams, ...result.params};
        this.frontendParams.pageSize = result.pageSize; // v parametrech na backendu neni strankovani
        this.frontendParams.pageNumber = result.pageNumber;

        this.pagination.setPageSize(result.pageSize);
        this.pagination.setTotalElements(result.totalElements);

        this.newSearchResultNotification$.next();

        return {
            items: result.content,
            pageNumber: result.pageNumber,
            first: result.first,
            last: result.last
        };
    }

    public reset() {

        this.pagination.reset();

        if (this.frontendParams.pageSize) {
            this.pagination.setPageSize(this.frontendParams.pageSize);
        }
    }


    public async newSearchWithParams(newParams: PARAMS | any): Promise<void> {
        this.frontendParams = newParams;
        await this.newSearch();
    }


    public async newSearch(): Promise<void> {
        this.reset();
        this.frontendParams.pageNumber = 1;
        await this.requestParametersChange(this.frontendParams);
    }


    public loadCurrentPage(): void {
        this.pagination.requestPage(this.frontendParams.pageNumber);
    }

    public setStaticParams(staticParams: any | StaticSearchParams): void {
        this.staticParams = staticParams;
    }

    public params(): PARAMS {
        return this.frontendParams;
    }

    public setParams(params: PARAMS): void {
        this.frontendParams = params;
    }

    public lastSearch(): ViewableSearch<PARAMS, ITEM> {
        return this.search;
    }

    public lastResult(): PagedParameteredResult<ITEM, PARAMS> {
        return this.search?.result;
    }

    public getForms(): Form[] {
        return this.forms;
    }

    public async loadForms(): Promise<void> {
        if (this.loadFormsFunction) {
            this.forms = await this.loadFormsFunction(this.staticParams);
        } else {
            await this.newSearchWithParams(this.staticParams);
        }
    }

    public getPagination(): any {
        return this.pagination;
    }

    public destroy(): void {
        this.deregisterUrlStateChange();
    }

    public getNewSearchResultNotification$(): Observable<void> {
        return this.newSearchResultNotification$.asObservable();
    }
}
